import React, { useEffect, useRef, useState } from 'react';
import {
  formatRecurringEnd,
  formatRecurringLabel,
  localTimeZone,
  ordinalDay,
  recurUnitLabel,
  scheduleRecurring,
} from '../../../utils/recurringSchedule';

const UNITS = ['minute', 'hour', 'day', 'week', 'month'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function digits(raw, maxLen) {
  return String(raw || '').replace(/\D/g, '').slice(0, maxLen);
}

function toDatetimeLocal(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocal(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function seedState(seed) {
  const today = new Date();
  return {
    every: seed?.every ? String(seed.every) : '1',
    unit: seed?.unit || 'day',
    time: seed?.time || '09:00',
    weekday: seed?.weekday != null ? seed.weekday : today.getDay(),
    monthDay: seed?.monthDay || today.getDate(),
    end: seed?.end || 'never',
    times: seed?.times ? String(seed.times) : '5',
    until: seed?.until ? toDatetimeLocal(seed.until) : '',
    timeZone: seed?.timeZone || localTimeZone(),
  };
}

function draftFromState(state) {
  const every = parseInt(state.every, 10);
  const draft = {
    every,
    unit: state.unit,
    end: state.end,
    timeZone: state.timeZone,
  };
  if (state.unit === 'day' || state.unit === 'week' || state.unit === 'month') {
    draft.time = state.time;
  }
  if (state.unit === 'week') draft.weekday = state.weekday;
  if (state.unit === 'month') draft.monthDay = state.monthDay;
  if (state.end === 'times') draft.times = parseInt(state.times, 10);
  if (state.end === 'until') draft.until = fromDatetimeLocal(state.until);
  return draft;
}

function previewFor(state, seed) {
  const draft = draftFromState(state);
  const scheduled = scheduleRecurring(draft, Date.now());
  if (!scheduled) {
    if (state.end === 'times') return 'Enter how many times';
    if (state.end === 'until' && !state.until) return 'Pick an end date';
    if (state.end === 'until') return 'End has to be after the first reminder';
    return 'Choose how often';
  }
  const label = formatRecurringLabel(scheduled.recur);
  const endRule = seed && seed.fired
    ? { ...scheduled.recur, fired: seed.fired }
    : scheduled.recur;
  const end = formatRecurringEnd(endRule);
  const when = new Date(scheduled.at).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${label}, ${end}. Next ${when}.`;
}

/**
 * Repeat rule for a mission reminder: interval, clock time, and when it stops.
 */
export default function RecurringFields({ seed = null, onChange }) {
  const [state, setState] = useState(() => seedState(seed));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const scheduled = scheduleRecurring(draftFromState(state), Date.now());
    if (!scheduled) {
      onChangeRef.current(null);
      return;
    }
    const rule = { ...scheduled.recur };
    delete rule.anchor;
    delete rule.fired;
    onChangeRef.current(rule);
  }, [state]);

  const set = (patch) => setState((prev) => ({ ...prev, ...patch }));
  const clockUnits = state.unit === 'day' || state.unit === 'week' || state.unit === 'month';

  return (
    <div className="date-time-popover__recur">
      <div className="date-time-popover__recur-line">
        <span className="date-time-popover__recur-prefix">Every</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          aria-label="Repeat interval"
          className="date-time-popover__recur-every"
          value={state.every}
          onChange={(e) => set({ every: digits(e.target.value, 3) })}
        />
        <select
          aria-label="Repeat unit"
          className="date-time-popover__recur-unit"
          value={state.unit}
          onChange={(e) => set({ unit: e.target.value })}
        >
          {UNITS.map((unit) => (
            <option key={unit} value={unit}>{recurUnitLabel(unit, state.every)}</option>
          ))}
        </select>
      </div>

      {clockUnits && (
        <div className="date-time-popover__recur-line">
          {state.unit === 'week' && (
            <select
              aria-label="Weekday"
              className="date-time-popover__recur-unit"
              value={state.weekday}
              onChange={(e) => set({ weekday: Number(e.target.value) })}
            >
              {WEEKDAYS.map((name, index) => (
                <option key={name} value={index}>{name}</option>
              ))}
            </select>
          )}
          {state.unit === 'month' && (
            <select
              aria-label="Day of month"
              className="date-time-popover__recur-unit"
              value={state.monthDay}
              onChange={(e) => set({ monthDay: Number(e.target.value) })}
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                <option key={day} value={day}>{ordinalDay(day)}</option>
              ))}
            </select>
          )}
          <span className="date-time-popover__recur-prefix">at</span>
          <input
            type="time"
            aria-label="Time of day"
            className="date-time-popover__recur-time"
            value={state.time}
            onChange={(e) => set({ time: e.target.value })}
          />
        </div>
      )}

      <div className="date-time-popover__recur-ends" role="group" aria-label="Ends">
        <button
          type="button"
          className={`date-time-popover__recur-end${state.end === 'never' ? ' is-selected' : ''}`}
          aria-pressed={state.end === 'never'}
          onClick={() => set({ end: 'never' })}
        >
          Until removed
        </button>
        <button
          type="button"
          className={`date-time-popover__recur-end${state.end === 'times' ? ' is-selected' : ''}`}
          aria-pressed={state.end === 'times'}
          onClick={() => set({ end: 'times' })}
        >
          A number of times
        </button>
        <button
          type="button"
          className={`date-time-popover__recur-end${state.end === 'until' ? ' is-selected' : ''}`}
          aria-pressed={state.end === 'until'}
          onClick={() => set({ end: 'until' })}
        >
          Until a date
        </button>
      </div>

      {state.end === 'times' && (
        <label className="date-time-popover__recur-line">
          <span className="date-time-popover__recur-prefix">Times</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            aria-label="Number of times"
            className="date-time-popover__recur-every"
            value={state.times}
            onChange={(e) => set({ times: digits(e.target.value, 3) })}
          />
        </label>
      )}

      {state.end === 'until' && (
        <input
          type="datetime-local"
          aria-label="Repeat until"
          className="date-time-popover__recur-until"
          value={state.until}
          onChange={(e) => set({ until: e.target.value })}
        />
      )}

      <p className="date-time-popover__recur-preview">{previewFor(state, seed)}</p>
    </div>
  );
}
